// @flow

// Looking for the actual PinRepWithImpressions? This file isn't it. This is a
// temporary experiment branch which replaces all uses of old
// PinRepWithImpressions with the new one for Lego treatment users. See
// web_grid_immersive experiment for more info.
// TODO: remove this branching logic when Lego PinRep ships to partners.

import React from 'react';
import { type BoardActivityItem } from 'app/common/redux/reducers/activityItems';
import { type Pin } from 'app/common/react/components/PinRep/types';
import { type Surface } from 'app/common/react/components/PinRep/core/PinRepConfiguration';
import DeprecatedPinRepWithImpressions, {
  type ExternalProps as DeprecatedPinRepWithImpressionsProps,
} from 'app/common/react/components/DeprecatedPinRep/DeprecatedPinRepWithImpressions';
import ExperimentalPinRepWithImpressions, {
  type ExternalProps as ExperimentalPinRepProps,
} from 'app/common/react/components/PinRep/core/PinRepWithImpressions';

type OwnProps = {|
  // Override to force using the old PinRep.
  // We'll continue to use the old PinRep for partners for the time being,
  // as well as certain other special cases. Please do *not* use this unless absolutely necessary.
  forceOldPinRep?: boolean,
|};

// These props are completely new additions that the old PinRep doesn't have.
// Still, since we're only using this one component as a branch to new or old
// PinRep, we need to spread new props into the old ones. All other props that
// still exist will either pass through or be transformed via
// transformPinRepPropsFromOldToNew below.
type CompletelyNewPinRepProps = {|
  additionalData?: {|
    boardActivityComments?: Array<BoardActivityItem>,
  |},
  blockClickEvents?: boolean,
  // Sadly some grids pass a non-standard pin object, so we need to allow for that. Ideally this goes away someday.
  pin: Pin,
  surface: Surface,
|};

type Props = {|
  ...CompletelyNewPinRepProps,
  ...DeprecatedPinRepWithImpressions,
  ...OwnProps,
|};

function transformPinRepPropsFromOldToNew(props: {
  ...DeprecatedPinRepWithImpressionsProps,
  ...CompletelyNewPinRepProps,
}) {
  const newProps: $Shape<ExperimentalPinRepProps> = {};

  newProps.allowImageCropping = props.truncatePinHeight;
  newProps.disableHover = props.hideHover;
  newProps.feedbackOptions = props.feedback;
  newProps.saveButtonOptions = props.saveOptions;
  newProps.blockClickEvents = props.blockClickEvents;

  // Sadly some grids pass a non-standard pin object, so we need to allow for that. Ideally this goes away someday.
  newProps.pin = props.pin;
  newProps.pinId = props.pin.id;

  newProps.imageOnlyOption = 'default';
  if (typeof props.showImageOnly === 'boolean') {
    newProps.imageOnlyOption = props.showImageOnly ? 'image-only' : 'not-image-only';
  }

  newProps.boardActivityOptions = 'default';
  if (props.showPinReactionsReply === false) {
    newProps.boardActivityOptions = 'no-reply';
  }

  // callbacks on interaction
  newProps.onError = props.onError;
  newProps.onLoad = props.onLoad;

  // tracking params
  newProps.trackingParameters = {
    componentType: props.componentType,
    contextLogData: props.contextLogData,
    impressionAuxFields: props.impressionAuxFields,
    slotIndex: props.slotIndex,
    viewData: props.viewData,
    viewParameter: props.viewParameter,
    viewType: props.viewType,
  };

  newProps.resolution = 'default';
  if (props.highResOnly) {
    newProps.resolution = 'high-res';
  } else if (props.lowResOnly) {
    newProps.resolution = 'low-res';
  }

  if (typeof props.selection !== 'undefined') {
    const selectionStateTransforms = {
      NO_SELECTION: 'no-selection',
      SELECTED: 'selected',
      UNSELECTED: 'deselected',
    };
    newProps.selectionState = selectionStateTransforms[props.selection];
  }

  newProps.attributionOption = null;
  if (props.showCredits || props.hideAttribution === false) {
    newProps.attributionOption = 'show';
  } else if (props.hideAttribution || props.showCredits === false) {
    newProps.attributionOption = 'hide';
  }

  // These props are new to ExperimentalPinRep:
  newProps.surface = props.surface;

  // Deprecated props:
  // createdAt?: string, // only used for following feed
  // onPinRepClick?: () => void, // only used for a shut-down experiment
  // pin: Object, // all uses of PinRep should now be normalized
  // showClickthroughButton?: boolean, // only used on mobile
  // showCondensedInfo?: boolean, // covered by surface-specific logic in PinRepConfiguration
  // showPinStats?: boolean, // covered by surface-specific logic in PinRepConfiguration
  // showProductInfo?: boolean, // used to display prices, which is not currently supported in Lego
  // showSavedInfo?: boolean, // handled by saveButtonOptions

  return newProps;
}

function PinRepWithImpressions({ forceOldPinRep, ...props }: Props) {
  if (forceOldPinRep) {
    // Early short-circuit if we're forcing the old PinRep
    // Props will pass through unmodified.
    return <DeprecatedPinRepWithImpressions {...props} />;
  }

  return (
    // $FlowFixMe - still figuring out the story with pass thru props
    <ExperimentalPinRepWithImpressions {...transformPinRepPropsFromOldToNew(props)} />
  );
}

export default PinRepWithImpressions;
